// Comparison Mode - Before/After as a Single Object
// Synchronized zoom, pan, and inspection controls

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { useGesture } from '@use-gesture/react';
import { useDrag } from '@use-gesture/react';
import type { ComparisonPair } from '@/types';

interface ComparisonViewProps {
  pair: ComparisonPair;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

interface ViewState {
  scale: number;
  x: number;
  y: number;
}

export function ComparisonView({
  pair,
  onClose,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
}: ComparisonViewProps) {
  // Synchronized view state - controls both images as one
  const [viewState, setViewState] = useState<ViewState>({
    scale: 1,
    x: 0,
    y: 0,
  });
  const [sliderPosition, setSliderPosition] = useState(50); // Wipe slider percentage
  const [viewMode, setViewMode] = useState<'side-by-side' | 'wipe'>('side-by-side');
  const [isSweeping, setIsSweeping] = useState(false);
  const [showVoiceHints, setShowVoiceHints] = useState(true);
  const sweepDirectionRef = useRef<1 | -1>(1); // 1 = moving right, -1 = moving left
  const sweepAnimationRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Gesture-based zoom and pan — works with both mouse and touch
  const bind = useGesture(
    {
      onPinch: ({ offset: [s] }) => {
        setViewState((prev) => ({ ...prev, scale: s }));
      },
      onPinchEnd: ({ offset: [s] }) => {
        if (s < 0.6) {
          setViewState({ scale: 0.5, x: 0, y: 0 });
        }
      },
      onDrag: ({ movement: [mx, my], memo = { startScale: viewState.scale } }) => {
        if (memo.startScale > 1) {
          setViewState((prev) => ({ ...prev, x: mx, y: my }));
        }
        return memo;
      },
      onWheel: ({ delta: [, dy] }) => {
        const factor = dy > 0 ? 0.9 : 1.1;
        setViewState((prev) => ({
          ...prev,
          scale: Math.min(Math.max(prev.scale * factor, 0.5), 5),
        }));
      },
    },
    {
      pinch: { scaleBounds: { min: 0.5, max: 5 }, rubberband: true },
      drag: { filterTaps: true, from: () => [viewState.x, viewState.y] },
    }
  );

  // Reset view
  const resetView = useCallback(() => {
    setViewState({ scale: 1, x: 0, y: 0 });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onPrevious?.();
          break;
        case 'ArrowRight':
          onNext?.();
          break;
        case '+':
        case '=':
          setViewState((prev) => ({
            ...prev,
            scale: Math.min(prev.scale * 1.2, 5),
          }));
          break;
        case '-':
          setViewState((prev) => ({
            ...prev,
            scale: Math.max(prev.scale / 1.2, 0.5),
          }));
          break;
        case '0':
          resetView();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext, resetView]);

  // Voice command handler for comparison view
  useEffect(() => {
    const handleVoiceCompare = (e: CustomEvent<{ action: string }>) => {
      const { action } = e.detail;

      switch (action) {
        case 'side-by-side':
          setViewMode('side-by-side');
          setIsSweeping(false);
          break;
        case 'wipe':
          setViewMode('wipe');
          break;
        case 'sweep-start':
          setViewMode('wipe');
          setIsSweeping(true);
          break;
        case 'sweep-stop':
          setIsSweeping(false);
          break;
        case 'slider-left':
          setViewMode('wipe');
          setIsSweeping(false);
          setSliderPosition((prev) => Math.max(0, prev - 25));
          break;
        case 'slider-right':
          setViewMode('wipe');
          setIsSweeping(false);
          setSliderPosition((prev) => Math.min(100, prev + 25));
          break;
        case 'slider-center':
          setViewMode('wipe');
          setIsSweeping(false);
          setSliderPosition(50);
          break;
      }
    };

    window.addEventListener('voice-compare', handleVoiceCompare as EventListener);
    return () => {
      window.removeEventListener('voice-compare', handleVoiceCompare as EventListener);
    };
  }, []);

  // Auto-sweep animation - slowly moves slider back and forth
  useEffect(() => {
    if (!isSweeping) {
      if (sweepAnimationRef.current) {
        cancelAnimationFrame(sweepAnimationRef.current);
        sweepAnimationRef.current = null;
      }
      return;
    }

    let lastTime = performance.now();
    const sweepSpeed = 15; // Percentage per second (slow, cinematic)

    const animate = (currentTime: number) => {
      const deltaTime = (currentTime - lastTime) / 1000; // Convert to seconds
      lastTime = currentTime;

      setSliderPosition((prev) => {
        let newPosition = prev + sweepDirectionRef.current * sweepSpeed * deltaTime;

        // Reverse direction at bounds
        if (newPosition >= 95) {
          newPosition = 95;
          sweepDirectionRef.current = -1;
        } else if (newPosition <= 5) {
          newPosition = 5;
          sweepDirectionRef.current = 1;
        }

        return newPosition;
      });

      sweepAnimationRef.current = requestAnimationFrame(animate);
    };

    sweepAnimationRef.current = requestAnimationFrame(animate);

    return () => {
      if (sweepAnimationRef.current) {
        cancelAnimationFrame(sweepAnimationRef.current);
      }
    };
  }, [isSweeping]);

  const imageStyle: React.CSSProperties = {
    transform: `translate(${viewState.x}px, ${viewState.y}px) scale(${viewState.scale})`,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-workshop-bg flex flex-col"
    >
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 bg-workshop-surface border-b border-workshop-border">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className={clsx(
              'flex items-center gap-2 px-4 py-3 rounded-xl transition-colors',
              'text-white hover:bg-workshop-border min-h-touch'
            )}
          >
            <BackIcon />
            <span>Back</span>
          </button>

          <div className="h-8 w-px bg-workshop-border" />

          {/* View mode toggle */}
          <div className="flex bg-workshop-bg rounded-lg p-1">
            <button
              onClick={() => setViewMode('side-by-side')}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                viewMode === 'side-by-side'
                  ? 'bg-workshop-accent text-white'
                  : 'text-gray-400 hover:text-white'
              )}
            >
              Side by Side
            </button>
            <button
              onClick={() => setViewMode('wipe')}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                viewMode === 'wipe'
                  ? 'bg-workshop-accent text-white'
                  : 'text-gray-400 hover:text-white'
              )}
            >
              Wipe Slider
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Sweep indicator */}
          {isSweeping && (
            <div className="flex items-center gap-2 px-3 py-2 bg-workshop-accent/20 border border-workshop-accent rounded-lg">
              <span className="w-2 h-2 bg-workshop-accent rounded-full animate-pulse" />
              <span className="text-workshop-accent text-sm font-medium">Sweeping</span>
              <button
                onClick={() => setIsSweeping(false)}
                className="text-workshop-accent hover:text-white ml-1"
              >
                <StopIcon />
              </button>
            </div>
          )}

          {/* Zoom controls */}
          <div className="flex items-center gap-2 bg-workshop-bg rounded-lg px-3 py-2">
            <button
              onClick={() =>
                setViewState((prev) => ({
                  ...prev,
                  scale: Math.max(prev.scale / 1.2, 0.5),
                }))
              }
              className="text-gray-400 hover:text-white p-1"
            >
              <MinusIcon />
            </button>
            <span className="text-white font-mono text-sm w-12 text-center">
              {Math.round(viewState.scale * 100)}%
            </span>
            <button
              onClick={() =>
                setViewState((prev) => ({
                  ...prev,
                  scale: Math.min(prev.scale * 1.2, 5),
                }))
              }
              className="text-gray-400 hover:text-white p-1"
            >
              <PlusIcon />
            </button>
            <button
              onClick={resetView}
              className="text-gray-400 hover:text-white p-1 ml-1"
              title="Reset view (0)"
            >
              <ResetIcon />
            </button>
          </div>

          {/* Navigation - grouped together for kiosk use */}
          {(hasPrevious || hasNext) && (
            <div className="flex items-center gap-2 bg-workshop-bg rounded-lg p-1">
              <button
                onClick={onPrevious}
                disabled={!hasPrevious}
                className={clsx(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  hasPrevious
                    ? 'text-white hover:bg-workshop-accent'
                    : 'text-gray-600 cursor-not-allowed'
                )}
              >
                ← Prev
              </button>
              <button
                onClick={onNext}
                disabled={!hasNext}
                className={clsx(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  hasNext
                    ? 'text-white hover:bg-workshop-accent'
                    : 'text-gray-600 cursor-not-allowed'
                )}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Voice command hints - floating panel */}
      {showVoiceHints && (
        <div className="absolute top-20 right-4 z-20 bg-workshop-surface/95 backdrop-blur border border-workshop-border rounded-xl p-4 shadow-xl max-w-xs">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MicIcon />
              <span className="text-white font-medium text-sm">Voice Commands</span>
            </div>
            <button
              onClick={() => setShowVoiceHints(false)}
              className="text-gray-400 hover:text-white"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="space-y-2 text-xs">
            <VoiceHint label="Play" description="Start auto-sweep" />
            <VoiceHint label="Stop" description="Stop animation" />
            <VoiceHint label="Wipe" description="Switch to slider mode" />
            <VoiceHint label="Split" description="Side by side view" />
            <VoiceHint label="Left / Before" description="Show before image" />
            <VoiceHint label="Right / After" description="Show after image" />
            <VoiceHint label="Back" description="Exit comparison" />
          </div>
        </div>
      )}

      {/* Show hints button when hidden */}
      {!showVoiceHints && (
        <button
          onClick={() => setShowVoiceHints(true)}
          className="absolute top-20 right-4 z-20 p-3 bg-workshop-surface/80 backdrop-blur border border-workshop-border rounded-xl hover:bg-workshop-accent transition-colors"
          title="Show voice commands"
        >
          <MicIcon />
        </button>
      )}

      {/* Main comparison area */}
      <div
        ref={containerRef}
        {...bind()}
        className={clsx(
          'flex-1 relative overflow-hidden',
          viewState.scale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
        )}
        style={{ touchAction: 'none' }}
      >
        {viewMode === 'side-by-side' ? (
          <SideBySideView
            pair={pair}
            imageStyle={imageStyle}
          />
        ) : (
          <WipeSliderView
            pair={pair}
            imageStyle={imageStyle}
            sliderPosition={sliderPosition}
            onSliderChange={setSliderPosition}
          />
        )}
      </div>

      {/* Footer with metadata - centered layout: BEFORE | COMPONENT | AFTER */}
      <footer className="bg-workshop-surface border-t border-workshop-border">
        <div className="flex items-stretch justify-center">
          {/* Before metadata */}
          <div className="p-4 text-right min-w-[200px] max-w-[280px]">
            <div className="flex items-center justify-end gap-2 mb-1">
              <span className="px-2 py-1 bg-orange-600/20 text-orange-400 rounded text-xs font-bold">
                BEFORE
              </span>
              <span className="text-gray-400 text-sm">
                {new Date(pair.beforeImage.capturedAt).toLocaleDateString()}
              </span>
            </div>
            <p className="text-white font-medium text-sm">{pair.beforeImage.componentDetail}</p>
            <p className="text-gray-400 text-xs capitalize">
              {pair.beforeImage.condition.replace('-', ' ')}
            </p>
          </div>

          {/* Component label - center */}
          <div className="flex items-center justify-center px-8 bg-workshop-bg border-x border-workshop-border">
            <div className="text-center">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Component</p>
              <p className="text-white font-semibold text-lg capitalize">{pair.component}</p>
            </div>
          </div>

          {/* After metadata */}
          <div className="p-4 text-left min-w-[200px] max-w-[280px]">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-xs font-bold">
                AFTER
              </span>
              <span className="text-gray-400 text-sm">
                {new Date(pair.afterImage.capturedAt).toLocaleDateString()}
              </span>
            </div>
            <p className="text-white font-medium text-sm">{pair.afterImage.componentDetail}</p>
            <p className="text-gray-400 text-xs capitalize">
              {pair.afterImage.condition.replace('-', ' ')}
            </p>
          </div>
        </div>
      </footer>
    </motion.div>
  );
}

/**
 * Side-by-side synchronized view
 */
function SideBySideView({
  pair,
  imageStyle,
}: {
  pair: ComparisonPair;
  imageStyle: React.CSSProperties;
}) {
  return (
    <div className="absolute inset-0 flex">
      {/* Before image */}
      <div className="flex-1 relative overflow-hidden border-r border-workshop-border">
        <div className="absolute top-4 left-4 z-10">
          <span className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm font-bold shadow-lg">
            BEFORE
          </span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-900">
          <img
            src={pair.beforeImage.url}
            alt={`Before: ${pair.beforeImage.componentDetail}`}
            className="max-w-full max-h-full object-contain transition-transform duration-100"
            style={imageStyle}
            draggable={false}
          />
        </div>
      </div>

      {/* After image */}
      <div className="flex-1 relative overflow-hidden">
        <div className="absolute top-4 left-4 z-10">
          <span className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-bold shadow-lg">
            AFTER
          </span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-900">
          <img
            src={pair.afterImage.url}
            alt={`After: ${pair.afterImage.componentDetail}`}
            className="max-w-full max-h-full object-contain transition-transform duration-100"
            style={imageStyle}
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Vertical wipe slider view - single image with split reveal
 */
function WipeSliderView({
  pair,
  imageStyle,
  sliderPosition,
  onSliderChange,
}: {
  pair: ComparisonPair;
  imageStyle: React.CSSProperties;
  sliderPosition: number;
  onSliderChange: (position: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Touch + mouse drag for slider — replaces window mousemove listeners
  const sliderBind = useDrag(
    ({ xy: [x], event }) => {
      event.stopPropagation();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const position = ((x - rect.left) / rect.width) * 100;
      onSliderChange(Math.max(0, Math.min(100, position)));
    },
    { axis: 'x' }
  );

  // Tap-to-position on the container background
  const handleContainerTap = useCallback(
    (e: React.PointerEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const position = ((e.clientX - rect.left) / rect.width) * 100;
      onSliderChange(Math.max(0, Math.min(100, position)));
    },
    [onSliderChange]
  );

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 bg-neutral-900"
      onPointerDown={handleContainerTap}
    >
      {/* After image (full width, behind) */}
      <div className="absolute inset-0 flex items-center justify-center">
        <img
          src={pair.afterImage.url}
          alt={`After: ${pair.afterImage.componentDetail}`}
          className="max-w-full max-h-full object-contain"
          style={imageStyle}
          draggable={false}
        />
        <div className="absolute top-4 right-4">
          <span className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-bold shadow-lg">
            AFTER
          </span>
        </div>
      </div>

      {/* Before image (clipped by slider) */}
      <div
        className="absolute inset-0 flex items-center justify-center overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img
          src={pair.beforeImage.url}
          alt={`Before: ${pair.beforeImage.componentDetail}`}
          className="max-w-full max-h-full object-contain"
          style={imageStyle}
          draggable={false}
        />
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm font-bold shadow-lg">
            BEFORE
          </span>
        </div>
      </div>

      {/* Slider handle — touch + mouse draggable */}
      <div
        {...sliderBind()}
        className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)', touchAction: 'none' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
          <SliderIcon />
        </div>
      </div>
    </div>
  );
}

// Icons
function BackIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <rect x="6" y="6" width="12" height="12" rx="1" />
    </svg>
  );
}

function SliderIcon() {
  return (
    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg className="w-4 h-4 text-workshop-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function VoiceHint({ label, description }: { label: string; description: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="px-2 py-0.5 bg-workshop-accent/20 text-workshop-accent rounded font-mono font-medium">
        "{label}"
      </span>
      <span className="text-gray-400">{description}</span>
    </div>
  );
}
